import { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  TableRow,
  TableCell,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import DataTable from '@/components/DataTable/DataTable';
import { formatTimestamp } from '@/utils/date/date';
import PaymentRailsChips from '@/components/MoovAccounts/PaymentRailsChips';
import { FboStatusBadge } from '@/components/FboStatusBadge';
import { MoovAccountsApi } from '@/api/clients/moov-accounts';
import MoovAccountFboDialog from './MoovAccountFboDialog';

const MoovAccountsList = ({ organizationId }) => {
  const { t } = useTranslation();
  const dataTableRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const handleEditFbo = (account) => {
    setSelectedAccount(account);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
  };

  const handleFboSave = async (formData) => {
    if (!selectedAccount) return;

    setLoading(true);
    try {
      await MoovAccountsApi.updateFboStatus(
        organizationId,
        selectedAccount.id,
        formData,
      );
      toast.success(t('moov.fboDialog.updateSuccess'), {
        position: 'bottom-right',
      });
      handleCloseDialog();
      dataTableRef.current?.refresh();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || t('moov.fboDialog.updateError');
      toast.error(errorMessage, {
        position: 'bottom-right',
      });
      console.error('Error updating FBO status:', err);
    } finally {
      setLoading(false);
    }
  };

  const columnFilters = useMemo(
    () => [
      {
        key: 'is_fbo',
        label: t('moov.filters.fboStatus'),
        filterType: 'select',
        options: [
          { value: '', label: t('moov.filters.allStatuses') },
          { value: 'true', label: t('moov.filters.fbo') },
          { value: 'false', label: t('moov.filters.nonFbo') },
        ],
      },
    ],
    [t],
  );

  const columns = [
    {
      key: 'account_name',
      label: t('moov.table.accountName'),
      sortable: true,
    },
    {
      key: 'account_id',
      label: t('moov.table.accountId'),
      sortable: true,
      render: (value) => (
        <Tooltip title={value}>
          <Typography
            variant='body2'
            sx={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'is_fbo',
      label: t('moov.table.fboStatus'),
      sortable: true,
      render: (value) => <FboStatusBadge isFbo={value} />,
    },
    {
      key: 'payment_rails',
      label: t('moov.table.paymentRails'),
      sortable: false,
      render: (_, row) => <PaymentRailsChips account={row} />,
    },
    {
      key: 'is_terms_and_condition',
      label: t('moov.table.termsAccepted'),
      sortable: true,
      render: (value) => (
        <Box>
          {value ? (
            <Typography variant='body2' color='success.main'>
              ✓ {t('moov.badges.yes')}
            </Typography>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              {t('moov.badges.no')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'created_at',
      label: t('moov.table.createdAt'),
      sortable: true,
      render: (value) => formatTimestamp(value),
    },
    {
      key: 'actions',
      label: t('moov.table.actions'),
      sortable: false,
      align: 'right',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title={t('moov.fboDialog.title')}>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleEditFbo(row);
              }}
              disabled={loading}>
              <Edit fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pt: 2 }}>
      <DataTable
        ref={dataTableRef}
        columns={columns}
        endpoint={`/organizations/${organizationId}/moov-accounts`}
        initialSort={{ key: 'created_at', order: 'desc' }}
        pageSize={10}
        autoFetch={true}
        enableColumnPicker={true}
        enableSorting={true}
        enableFilters={true}
        columnFilters={columnFilters}
        externalLoading={loading}
        renderEmptyState={() => (
          <TableRow>
            <TableCell colSpan={7} align='center' sx={{ py: 8 }}>
              <Typography variant='body1' color='text.secondary'>
                {t('moov.emptyState.title')}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                {t('moov.emptyState.description')}
              </Typography>
            </TableCell>
          </TableRow>
        )}
      />

      <MoovAccountFboDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        account={selectedAccount}
        organizationId={organizationId}
        onSuccess={handleFboSave}
        loading={loading}
        setLoading={setLoading}
      />
    </Box>
  );
};

export default MoovAccountsList;
